import React, { useEffect, useState } from 'react';
import DocumentHeader from '../components/DocumentHeader';
import PaymentDetails from '../components/PaymentDetails';
import ContactPicker from '../components/ContactPicker';
import ProductPicker from '../components/ProductPicker';
import api from '../api/client';
import { useAuth } from '../hooks/useAuth';

interface Props { saleId: number; onBack: () => void; }

export default function SaleDetail({ saleId, onBack }: Props) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('sales.edit');
  const canVoid = hasPermission('sales.void');
  const [sale, setSale] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCorrect, setShowCorrect] = useState(false);
  const [mode, setMode] = useState<'menu'|'edit'|'void'>('menu');
  const [editItems, setEditItems] = useState<any[]>([]);
  const [editDate, setEditDate] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [voidDate, setVoidDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [editContact, setEditContact] = useState('');
  const [editBranch, setEditBranch] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editInvDiscount, setEditInvDiscount] = useState('');
  const [branches, setBranches] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [editSplits, setEditSplits] = useState<any[]>([]);

  const load = () => api.get(`/sales/${saleId}`).then(r => { setSale(r.data.data); setLoading(false); });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [saleId]);

  const openCorrect = () => {
    setMode('menu'); setMsg('');
    setEditDate(sale.sale_date || '');
    setVoidDate(sale.sale_date || '');
    setVoidReason('');
    setEditItems((sale.items || []).map((i:any) => ({
      product_id: i.product_id, location_id: i.location_id,
      product_name: i.product_name, location_name: i.location_name,
      qty: String(i.qty), unit_price: String(i.unit_price), discount: String(i.discount || 0),
    })));
    setEditContact(sale.contact_id ? String(sale.contact_id) : '');
    setEditBranch(sale.branch_id ? String(sale.branch_id) : '');
    setEditNotes(sale.notes || '');
    setEditInvDiscount(Number(sale.invoice_discount || 0) > 0 ? String(sale.invoice_discount) : '');
    setEditSplits((sale.payment_lines || []).map((p:any) => ({ account_id: String(p.account_id), amount: String(p.debit) })));
    api.get('/settings/branches').then(r => setBranches(r.data.data || [])).catch(() => {});
    api.get('/settings/locations').then(r => setLocations(r.data.data || [])).catch(() => {});
    api.get('/coa').then(r => setAccounts((r.data.data || []).filter((a:any) => String(a.account_code).startsWith('1')))).catch(() => {});
    setShowCorrect(true);
  };

  const addEditLine = () => setEditItems(l => [...l, { product_id:'', location_id:'', product_name:'', qty:'1', unit_price:'0', discount:'0' }]);
  const removeEditLine = (i:number) => setEditItems(l => l.filter((_,idx) => idx !== i));
  const addSplit = () => setEditSplits(s => [...s, { account_id:'', amount:'' }]);
  const removeSplit = (i:number) => setEditSplits(s => s.filter((_,idx) => idx !== i));
  const editLineTotal = editItems.reduce((s:number,i:any) => s + (Number(i.qty||0)*Number(i.unit_price||0) - Number(i.discount||0)), 0);
  const editTotal = Math.max(0, editLineTotal - Number(editInvDiscount || 0));
  const splitTotal = editSplits.reduce((s:number,x:any) => s + Number(x.amount||0), 0);
  const splitMismatch = editSplits.length > 0 && Math.abs(splitTotal - editTotal) > 0.005;

  useEffect(() => {
    if (editSplits.length === 1) {
      const want = editTotal.toFixed(2);
      if (editSplits[0].amount !== want) setEditSplits([{ ...editSplits[0], amount: want }]);
    }
    /* eslint-disable-next-line */
  }, [editTotal]);

  const saveEdit = async () => {
    setSaving(true); setMsg('');
    try {
      const items = editItems.map(i => ({
        product_id: Number(i.product_id), location_id: Number(i.location_id),
        qty: Number(i.qty), unit_price: Number(i.unit_price), discount: Number(i.discount || 0),
      }));
      const payload: any = { items, sale_date: editDate || undefined, notes: editNotes, invoice_discount: Number(editInvDiscount || 0) };
      if (editContact) payload.contact_id = Number(editContact);
      if (editBranch) payload.branch_id = Number(editBranch);
      const validSplits = editSplits.filter(s => s.account_id && Number(s.amount) > 0);
      if (validSplits.length) payload.splits = validSplits.map(s => ({ account_id: Number(s.account_id), amount: Number(s.amount) }));
      const res = await api.put(`/sales/${saleId}`, payload);
      setMsg(res.data.message || 'Updated');
      setShowCorrect(false);
      await load();
    } catch (e: any) { setMsg(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const doVoid = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await api.post(`/sales/${saleId}/void`, { reason: voidReason || undefined, reversal_date: voidDate || undefined });
      setMsg(res.data.message || 'Voided');
      setShowCorrect(false);
      await load();
    } catch (e: any) { setMsg(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const print = () => window.print();
  const fmt = (n: any) => Number(n||0).toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 });

  if (loading) return <div className="empty-state"><p>Loading...</p></div>;
  if (!sale) return <div className="empty-state"><p>Not found.</p></div>;

  return (
    <div>
      <div className="page-header" id="no-print">
        <button className="btn btn-secondary" onClick={onBack}>Back</button>
        <div style={{display:'flex',gap:8}}>
          {sale.status !== 'Cancelled' && (canEdit || canVoid) && <button className="btn btn-secondary" onClick={openCorrect}>Correct</button>}
          <button className="btn btn-primary" onClick={print}>Print</button>
        </div>
      </div>
      {showCorrect && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} id="no-print">
          <div className="card" style={{width: mode==='edit' ? 720 : 460, maxHeight:'85vh', overflowY:'auto'}}>
            <div className="card-header">
              <span>Correct {sale.sale_no}</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCorrect(false)}>Close</button>
            </div>
            <div className="card-body">
              {msg && <div style={{marginBottom:12,padding:8,background:'var(--sur2)',borderRadius:4,fontSize:12}}>{msg}</div>}

              {mode === 'menu' && (
                <div>
                  <div style={{marginBottom:16,fontSize:13,color:'var(--tx2)'}}>What would you like to do?</div>
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {canEdit && <button className="btn btn-secondary" style={{textAlign:'left',padding:'12px 14px'}} onClick={() => setMode('edit')}>
                      <div style={{fontWeight:600}}>Edit this transaction</div>
                      <div style={{fontSize:11,color:'var(--tx3)',marginTop:2}}>Change quantity, price, discount or date. Keeps the same document number.</div>
                    </button>}
                    {canVoid && <button className="btn btn-secondary" style={{textAlign:'left',padding:'12px 14px'}} onClick={() => setMode('void')}>
                      <div style={{fontWeight:600}}>Void this transaction</div>
                      <div style={{fontSize:11,color:'var(--tx3)',marginTop:2}}>Cancel it entirely. Stock and accounting are reversed.</div>
                    </button>}
                  </div>
                </div>
              )}

              {mode === 'edit' && (
                <div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10}}>
                    <div className="form-group">
                      <label className="form-label">Date</label>
                      <input type="date" className="form-control" value={editDate} max={new Date().toISOString().split('T')[0]} onChange={e => setEditDate(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Customer</label>
                      <ContactPicker value={editContact} initialLabel={sale.customer_name} placeholder="Walk-in / type to search" onSelect={(c:any) => setEditContact(String(c.id))} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Branch</label>
                      <select className="form-control" value={editBranch} onChange={e => setEditBranch(e.target.value)}>
                        <option value="">Select branch</option>
                        {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <input className="form-control" value={editNotes} onChange={e => setEditNotes(e.target.value)} />
                  </div>
                  <table style={{width:'100%',borderCollapse:'collapse',marginTop:8}}>
                    <thead><tr style={{background:'var(--sur2)'}}>
                      <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Product</th>
                      <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Qty</th>
                      <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Price</th>
                      <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Discount</th>
                      <th style={{padding:'8px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Total</th>
                    </tr></thead>
                    <tbody>
                      {editItems.map((it, idx) => (
                        <tr key={idx} style={{borderBottom:'1px solid var(--bdr)'}}>
                          <td style={{padding:'6px 6px',fontSize:12,minWidth:240}}>
                            <ProductPicker
                              value={it.product_id}
                              locationId={it.location_id}
                              initialLabel={it.product_name}
                              fontSize={12}
                              onSelect={(p:any) => setEditItems(list => list.map((x,ix) => ix===idx ? {...x, product_id:String(p.id), product_name:p.name, unit_price:String(p.selling_price ?? x.unit_price)} : x))}
                            />
                            <select className="form-control" style={{marginTop:4,fontSize:11}} value={it.location_id}
                              onChange={e => setEditItems(list => list.map((x,ix) => ix===idx ? {...x, location_id:e.target.value} : x))}>
                              <option value="">Select location</option>
                              {locations.map((l:any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                          </td>
                          <td style={{padding:'6px 4px',width:90}}>
                            <input className="form-control mono" type="text" inputMode="decimal" value={it.qty} style={{textAlign:'right'}}
                              onChange={e => setEditItems(list => list.map((x,i) => i===idx ? {...x, qty:e.target.value} : x))} />
                          </td>
                          <td style={{padding:'6px 4px',width:110}}>
                            <input className="form-control mono" type="text" inputMode="decimal" value={it.unit_price} style={{textAlign:'right'}}
                              onChange={e => setEditItems(list => list.map((x,i) => i===idx ? {...x, unit_price:e.target.value} : x))} />
                          </td>
                          <td style={{padding:'6px 4px',width:100}}>
                            <input className="form-control mono" type="text" inputMode="decimal" value={it.discount} style={{textAlign:'right'}}
                              onChange={e => setEditItems(list => list.map((x,i) => i===idx ? {...x, discount:e.target.value} : x))} />
                          </td>
                          <td style={{padding:'6px 10px',textAlign:'right',fontFamily:'monospace',fontWeight:600,fontSize:12}}>
                            {fmt(Number(it.qty||0)*Number(it.unit_price||0) - Number(it.discount||0))}
                          </td>
                          <td style={{padding:'6px 4px',width:30,textAlign:'center'}}>
                            {editItems.length > 1 && <button className="btn btn-secondary btn-sm" onClick={() => removeEditLine(idx)} style={{padding:'2px 6px'}}>x</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{marginTop:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <button className="btn btn-secondary btn-sm" onClick={addEditLine}>+ Add Line</button>
                    <div style={{display:'flex',alignItems:'center',gap:12}}>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <span style={{fontSize:12,color:'var(--tx3)'}}>Discount</span>
                        <input className="form-control mono" style={{width:100,textAlign:'right',fontSize:12}} value={editInvDiscount}
                          onChange={e => setEditInvDiscount(e.target.value)} placeholder="0.00" />
                      </div>
                      {Number(editInvDiscount||0) > 0 && <span style={{fontSize:12,color:'var(--tx3)',textDecoration:'line-through'}}>{fmt(editLineTotal)}</span>}
                      <div style={{fontWeight:700,fontSize:13}}>Total: {fmt(editTotal)}</div>
                    </div>
                  </div>

                  {editSplits.length > 0 && (
                    <div style={{marginTop:14,paddingTop:12,borderTop:'1px solid var(--bdr)'}}>
                      <div style={{fontSize:12,fontWeight:600,marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <span>Payment Accounts</span>
                        <button className="btn btn-secondary btn-sm" onClick={addSplit}>+ Add</button>
                      </div>
                      {editSplits.map((sp:any, si:number) => (
                        <div key={si} style={{display:'flex',gap:8,marginBottom:6,alignItems:'center'}}>
                          <select className="form-control" style={{flex:1,fontSize:12}} value={sp.account_id}
                            onChange={e => setEditSplits(list => list.map((x,ix) => ix===si ? {...x, account_id:e.target.value} : x))}>
                            <option value="">Select account</option>
                            {accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>)}
                          </select>
                          <input className="form-control mono" style={{width:120,textAlign:'right',fontSize:12}} value={sp.amount}
                            onChange={e => setEditSplits(list => list.map((x,ix) => ix===si ? {...x, amount:e.target.value} : x))} />
                          {editSplits.length > 1 && <button className="btn btn-secondary btn-sm" onClick={() => removeSplit(si)} style={{padding:'2px 6px'}}>x</button>}
                        </div>
                      ))}
                    </div>
                  )}
                  {editSplits.length === 0 && (
                    <div style={{marginTop:12}}>
                      <button className="btn btn-secondary btn-sm" onClick={addSplit}>+ Set payment accounts</button>
                    </div>
                  )}

                  {splitMismatch && (
                    <div style={{marginTop:10,padding:8,background:'var(--sur2)',borderRadius:4,fontSize:12,color:'var(--red)'}}>
                      Payment accounts total {fmt(splitTotal)} but the transaction total is {fmt(editTotal)}. Adjust the amounts before saving.
                    </div>
                  )}
                  <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}>
                    <button className="btn btn-secondary" onClick={() => setMode('menu')}>Back</button>
                    <button className="btn btn-primary" onClick={saveEdit} disabled={saving || splitMismatch}>{saving ? 'Saving...' : 'Save changes'}</button>
                  </div>
                </div>
              )}

              {mode === 'void' && (
                <div>
                  <div style={{marginBottom:14,fontSize:13}}>
                    This will reverse the accounting and return the stock. {sale.sale_no} will be marked Cancelled.
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reversal date</label>
                    <input type="date" className="form-control" value={voidDate} max={new Date().toISOString().split('T')[0]} onChange={e => setVoidDate(e.target.value)} style={{width:180}} />
                    <div style={{fontSize:11,color:'var(--tx3)',marginTop:4}}>Defaults to the original transaction date.</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reason</label>
                    <input className="form-control" value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="Optional" />
                  </div>
                  <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}>
                    <button className="btn btn-secondary" onClick={() => setMode('menu')}>Back</button>
                    <button className="btn btn-primary" onClick={doVoid} disabled={saving} style={{background:'var(--red)',borderColor:'var(--red)'}}>{saving ? 'Voiding...' : 'Void transaction'}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="card" id="print-area">
        <DocumentHeader
          branchName={sale.branch_name}
          branchAddress={sale.branch_address}
          branchPhone={sale.branch_phone}
          branchEmail={sale.branch_email}
          documentTitle={sale.sale_type}
        /><PaymentDetails />
        <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--bdr)', display:'flex', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:20, fontWeight:700 }}>{sale.sale_no}</div>
            <div style={{ fontSize:12, color:'var(--tx3)', marginTop:4 }}>{sale.sale_type} &mdash; {sale.status}</div>
          </div>
          <div style={{ textAlign:'right', fontSize:12, color:'var(--tx3)' }}>
            <div>Date: {new Date(sale.sale_date || sale.created_at).toLocaleDateString()}</div>
            {sale.due_date && <div>Due: {new Date(sale.due_date).toLocaleDateString()}</div>}
            <div>Branch: {sale.branch_name}</div>
          </div>
        </div>
        <div style={{ padding:'16px 24px', borderBottom:'1px solid var(--bdr)' }}>
          <div style={{ fontSize:12, color:'var(--tx3)', marginBottom:4 }}>Customer</div>
          <div style={{ fontWeight:600 }}>{sale.customer_name || 'Walk-in'}</div>
        </div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'var(--sur2)' }}>
              <th style={{ padding:'10px 16px', fontSize:11, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>Product</th>
              <th style={{ padding:'10px 16px', fontSize:11, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' }}>Location</th>
              <th style={{ padding:'10px 16px', fontSize:11, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Qty</th>
              <th style={{ padding:'10px 16px', fontSize:11, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Unit Price</th>
              <th style={{ padding:'10px 16px', fontSize:11, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Discount</th>
              <th style={{ padding:'10px 16px', fontSize:11, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(sale.items||[]).map((item: any, i: number) => (
              <tr key={i} style={{ borderBottom:'1px solid var(--bdr)' }}>
                <td style={{ padding:'10px 16px', fontWeight:500 }}>{item.product_name}</td>
                <td style={{ padding:'10px 16px', fontSize:11, color:'var(--tx3)' }}>{item.location_name}</td>
                <td style={{ padding:'10px 16px', textAlign:'right' }}>{Number(item.qty).toLocaleString()}</td>
                <td style={{ padding:'10px 16px', textAlign:'right' }}>{fmt(item.unit_price)}</td>
                <td style={{ padding:'10px 16px', textAlign:'right', color:'var(--red)' }}>{fmt(item.discount)}</td>
                <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:600 }}>{fmt(item.line_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop:'2px solid var(--bdr)', background:'var(--sur2)' }}>
              <td colSpan={5} style={{ padding:'12px 16px', fontWeight:700, textAlign:'right' }}>Total</td>
              <td style={{ padding:'12px 16px', fontWeight:700, fontSize:16, textAlign:'right', color:'var(--green)' }}>{fmt(sale.total_amount)}</td>
            </tr>
          </tfoot>
        </table>
        {sale.notes && <div style={{ padding:'12px 24px', fontSize:12, color:'var(--tx3)' }}>Notes: {sale.notes}</div>}
      </div>
      <style>{`@media print { #no-print { display:none !important; } }`}</style>
    </div>
  );
}